<template>
  <div class="tw-max-h-full">
    <WdsTabs
      v-if="shouldDisplayToggle"
      :active-tab-key="toggledOption"
      @update:active-tab-key="handleToggle"
    >
      <WdsTab :title="$t('Company contacts')" :tab-key="TrustedContactOrigin.COMPANY_CONTACTS" />
      <WdsTab
        v-if="hasOrgs"
        :title="$t('Group contacts')"
        :tab-key="TrustedContactOrigin.ORGANISATION_CONTACTS"
      />
    </WdsTabs>

    <GraphqlDataTable
      :key="forOrganisation ? 'organisation-contacts' : 'company-contacts'"
      v-model:order-by="orderBy"
      v-model:filters="filters"
      v-model:total-count="totalCount"
      v-model:selected-rows="selectedRows"
      :query
      :additional-query-variables
      :filter-black-list="filterBlackList"
      :column-black-list="['TrustedContactActionsColumn']"
      :max-height-adjustment="maxHeightAdjustment"
      :quick-filters="['statuses', 'countries']"
      :search-placeholder
      :sort-options="trustedContactsSortOptions"
      :multi-select
      :initial-selected-ids
      @row-click="$emit('row-click', $event)"
      @full-screen="$emit('full-screen', $event)"
    >
      <template v-if="$slots.action" #action="{ row }">
        <slot name="action" :row="row" />
      </template>
      <template #no-results>
        <slot name="no-results">
          {{ $t('No matching records found') }}
        </slot>
      </template>
    </GraphqlDataTable>
  </div>
</template>

<script lang="ts" setup>
import { computed, provide, ref, watch } from 'vue'
import { WdsTabs, WdsTab } from '@worksome/wds-components'
import { TypedQueryDocumentNode } from 'graphql/index'
import { graphql } from '@/gql'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import { TrustedContactOrigin } from '@/components/Contacts/CompanyFreelancer/types'
import { $t } from '@/composables/translation'
import { ContactStatus } from '@/gql/graphql'
import { DefaultStatuses } from '@/components/GraphqlDataTable/filters/trustedContacts/injection'
import { useAccountsStore } from '@/components/GraphqlDataTable/filters/accounts/accounts-store'
import {
  trustedContactsSortOptions,
  trustedContactsDefaultOrderBy,
} from '@/components/GraphqlDataTable/columns/TrustedContacts'

const emit = defineEmits<{
  'row-click': [row: any]
  'tab-toggled': [option: TrustedContactOrigin]
  'full-screen': [mode: boolean]
  'rows-selected': [rows: any[]]
  edit: [row: any]
  delete: [row: any]
  're-invite': [row: any]
  approved: []
  'approve-error': [errors: string[]]
}>()

const props = withDefaults(
  defineProps<{
    searchPlaceholder?: string
    multiSelect?: boolean
    preSelectedTrustedContacts?: { id: string; workerId: string }[]
    maxHeightAdjustment?: number | string
  }>(),
  {
    searchPlaceholder: $t('Search name, email, job title...'),
    multiSelect: false,
    preSelectedTrustedContacts: () => [],
    maxHeightAdjustment: 30,
  },
)

const orderBy = ref({ ...trustedContactsDefaultOrderBy })

const accountsStore = useAccountsStore()

const toggledOption = ref(TrustedContactOrigin.COMPANY_CONTACTS)

const divisions = computed(() =>
  accountsStore.accounts
    .filter((account) => account.id !== currentAccount.globalId)
    .map((account) => account.id),
)

const hasOrgs = computed(() => divisions.value.length > 0)

const forOrganisation = computed(
  () => toggledOption.value === TrustedContactOrigin.ORGANISATION_CONTACTS,
)

const shouldDisplayToggle = computed(() => divisions.value.length > 0)

const accounts = computed(() =>
  forOrganisation.value ? divisions.value : [currentAccount.globalId],
)

const defaultStatuses = ref<string[]>([ContactStatus.Active, ContactStatus.Invited])
provide(DefaultStatuses, defaultStatuses)

const filters = ref<Record<string, unknown>>({
  statuses: [ContactStatus.Active, ContactStatus.Invited],
})

const totalCount = defineModel<number | undefined>('totalCount')

const selectedRows = ref<Record<string, object>>({})

const initialSelectedIds = computed(() => props.preSelectedTrustedContacts.map((t) => t.id))

watch(
  selectedRows,
  (val) => {
    if (val) {
      emit('rows-selected', Object.values(val))
    }
  },
  { deep: true },
)

const additionalQueryVariables = computed(() => ({
  companyId: currentAccount.globalId,
  accounts: accounts.value,
}))

const filterBlackList = computed(() => ['accounts', 'companyId', 'states'])

const handleToggle = (optionKey: TrustedContactOrigin) => {
  toggledOption.value = optionKey
  emit('tab-toggled', optionKey)
}

// Company contacts query
const companyContactsQuery = graphql(`
  query CandidatesTrustedContactsList(
    $accounts: [ID!]
    $page: Int!
    $first: Int!
    $search: String
    $statuses: [ContactStatus!]
    $skills: [ID!]
    $hireHistory: HireHistoryFilterInput
    $hireStatus: HireStatusFilterInput
    $businessSetup: [BusinessEntityType!]
    $invitedByUsers: InvitedByInput
    $origin: TrustedContactOrigin
    $countries: [String!]
    $states: [String!]
    $orderBy: [TrustedContactOrderByClauseInput!]
    $companyId: ID!
    $customFields: [CustomFieldTypeValueInput!]
  ) {
    trustedContacts(
      accounts: $accounts
      page: $page
      first: $first
      search: $search
      statuses: $statuses
      skills: $skills
      origin: $origin
      invitedByUsers: $invitedByUsers
      businessSetup: $businessSetup
      countries: $countries
      states: $states
      orderBy: $orderBy
      hireHistory: $hireHistory
      hireStatus: $hireStatus
      customFields: $customFields
    ) {
      data {
        ...TrustedContactWorkerColumn
        ...TrustedContactSkillsColumn
        ...TrustedContactJobsCountColumn
        ...TrustedContactLocationColumn
        ...TrustedContactBusinessSetupColumn
        ...TrustedContactNotesColumn
      }
    }
  }
`) as TypedQueryDocumentNode

// Organisation contacts query
const organisationContactsQuery = graphql(`
  query CandidatesOrganisationTrustedContactsList(
    $accounts: [ID!]
    $page: Int!
    $first: Int!
    $search: String
    $statuses: [ContactStatus!]
    $skills: [ID!]
    $hireHistory: HireHistoryFilterInput
    $hireStatus: HireStatusFilterInput
    $businessSetup: [BusinessEntityType!]
    $invitedByUsers: InvitedByInput
    $origin: TrustedContactOrigin
    $countries: [String!]
    $states: [String!]
    $orderBy: [TrustedContactOrderByClauseInput!]
    $companyId: ID!
    $customFields: [CustomFieldTypeValueInput!]
  ) {
    organisationTrustedContacts(
      accounts: $accounts
      page: $page
      first: $first
      search: $search
      statuses: $statuses
      skills: $skills
      origin: $origin
      invitedByUsers: $invitedByUsers
      businessSetup: $businessSetup
      countries: $countries
      states: $states
      orderBy: $orderBy
      hireHistory: $hireHistory
      hireStatus: $hireStatus
      customFields: $customFields
    ) {
      data {
        ...TrustedContactWorkerColumn
        ...TrustedContactSkillsColumn
        ...TrustedContactJobsCountColumn
        ...TrustedContactLocationColumn
        ...TrustedContactBusinessSetupColumn
        ...TrustedContactNotesColumn
        ...TrustedContactOriginatingCompanyColumn
      }
    }
  }
`) as TypedQueryDocumentNode

// Switch query based on toggle state
const query = computed(() =>
  forOrganisation.value ? organisationContactsQuery : companyContactsQuery,
)
</script>

<style scoped lang="scss"></style>
